/**
 * CollabSession — 동시 편집 세션 lifecycle facade
 *
 * 컨트롤러에서 호출하는 단일 진입점.
 * Y.Doc 생성 → CableProvider 연결 → YEditorBinding 활성화 → Awareness 설정
 *
 * @example
 *   const session = new CollabSession({
 *     editor,
 *     recordType: 'Post',
 *     recordId: 123,
 *     userName: 'Alice',
 *     userColor: '#e06c75',
 *   })
 *   session.connect()
 *   // ...
 *   session.destroy()
 */
import * as Y from 'yjs'
import { seedToColor } from './color_utils'
import { Awareness } from 'y-protocols/awareness'
import { CableProvider } from './cable_provider'
import { YEditorBinding } from './y_editor_binding'

export class CollabSession {
  /**
   * @param {Object} options
   * @param {Object} options.editor - EditorJS 인스턴스
   * @param {string} options.recordType - 'Post' | 'Page'
   * @param {number} options.recordId
   * @param {string} options.userName
   * @param {string} [options.userColor]
   * @param {string} [options.userImage]
   * @param {Function} [options.onSynced] - 초기 동기화 완료 시 콜백
   * @param {Function} [options.onStatus] - 연결 상태 변경 콜백 ('connecting' | 'connected' | 'disconnected')
   * @param {Function} [options.onPresence] - 참여자 변경 콜백
   */
  constructor({ editor, recordType, recordId, editorId, userName, userColor, userImage, nestedEditorRegistry, onSynced, onStatus, onPresence, onConnected }) {
    this.editor = editor
    this.recordType = recordType
    this.recordId = recordId
    this.editorId = editorId
    this.nestedEditorRegistry = nestedEditorRegistry || new Map()
    this.destroyed = false

    // Y.Doc
    this.doc = new Y.Doc()
    this.yBlocks = this.doc.getArray('blocks')
    this.yMeta = this.doc.getMap('meta')

    // Awareness
    this.awareness = new Awareness(this.doc)
    this.awareness.setLocalStateField('user', {
      name: userName,
      color: userColor || seedToColor(userName),
      image: userImage || null,
      editorId: editorId || null,
    })

    // Undo Manager (local origin만 undo)
    this.undoManager = new Y.UndoManager(this.yBlocks, {
      trackedOrigins: new Set([null]), // null origin = 로컬 변경
    })

    // Provider
    this.provider = new CableProvider({
      doc: this.doc,
      awareness: this.awareness,
      recordType,
      recordId,
      editorId,
      onSynced: () => {
        this._onInitialSync()
        onSynced?.()
      },
      onStatus,
      onPresence,
      onConnected,
    })

    this.binding = null
  }

  connect() {
    this.provider.connect()
  }

  disconnect() {
    this.provider.disconnect()
  }

  destroy() {
    if (this.destroyed) return
    this.destroyed = true

    this.binding?.destroy()
    this.undoManager.destroy()
    this.awareness.destroy()
    this.provider.destroy()
    this.doc.destroy()
  }

  /**
   * Cmd+Z 처리
   */
  undo() {
    this.undoManager.undo()
  }

  /**
   * Cmd+Shift+Z 처리
   */
  redo() {
    this.undoManager.redo()
  }

  // --- Private ---

  _onInitialSync() {
    if (this.destroyed) return

    // 초기 sync 후 binding 활성화
    this.binding = new YEditorBinding({
      editor: this.editor,
      doc: this.doc,
      yBlocks: this.yBlocks,
      awareness: this.awareness,
      provider: this.provider,
      nestedEditorRegistry: this.nestedEditorRegistry,
      undoManager: this.undoManager,
    })
  }

}
